/**
 * Shared pieces of the on-device prediction engine (spec section 7).
 *
 * Everything here is pure: no storage, no clock reads. Callers pass the
 * prediction time explicitly so the same code serves live predictions and
 * walk-forward backtests without future leakage.
 */
import type { LocalEvent } from '@/lib/schemas';

export const MINUTE_MS = 60_000;
export const DAY_MS = 24 * 60 * MINUTE_MS;

export interface Sample {
  /** Observed value in minutes. */
  value: number;
  /** Non-negative recency weight. */
  weight: number;
}

/** Likely window around a point estimate, all as UTC epoch ms. */
export interface PredictionWindow {
  pointMs: number;
  startMs: number;
  endMs: number;
}

/**
 * Records as they were known at `nowMs`: drops records that start later and
 * reopens sleeps that had not yet ended, so a backtest never sees the future.
 * Tombstoned records are excluded.
 */
export function asOf(events: LocalEvent[], nowMs: number): LocalEvent[] {
  const out: LocalEvent[] = [];
  for (const e of events) {
    if (e.deleted_at || e.started_ms > nowMs) continue;
    if (e.ended_ms !== null && e.ended_ms > nowMs) out.push({ ...e, ended_ms: null, ended_at: null });
    else out.push(e);
  }
  return out;
}

/** Exponential recency weight: 1 now, 0.5 after one half-life. */
export function recencyWeight(ageMs: number, halfLifeDays: number): number {
  return Math.pow(0.5, Math.max(0, ageMs) / (halfLifeDays * DAY_MS));
}

/**
 * Weighted empirical quantile (lower step definition): the smallest value
 * whose cumulative weight reaches q of the total. With equal weights this is
 * the ordinary order statistic at ceil(q * n).
 */
export function weightedQuantile(samples: Sample[], q: number): number {
  if (!samples.length) throw new Error('weightedQuantile needs at least one sample');
  const sorted = [...samples].sort((a, b) => a.value - b.value);
  const total = sorted.reduce((s, x) => s + x.weight, 0);
  if (!(total > 0)) throw new Error('weightedQuantile needs positive total weight');
  const target = Math.min(1, Math.max(0, q)) * total;
  let cumulative = 0;
  for (const s of sorted) {
    cumulative += s.weight;
    if (cumulative >= target - 1e-12) return s.value;
  }
  return sorted[sorted.length - 1].value;
}

/** Kish effective sample size of a weighted sample. */
export function effectiveSampleSize(samples: Sample[]): number {
  const sum = samples.reduce((s, x) => s + x.weight, 0);
  const sumSq = samples.reduce((s, x) => s + x.weight * x.weight, 0);
  return sumSq > 0 ? (sum * sum) / sumSq : 0;
}

export interface WindowOptions {
  lowerQ: number;
  upperQ: number;
  minSamples: number;
  /** Each side of the window is at least this wide, so regular data never yields a zero-width "certain" window. */
  minHalfWidthMin?: number;
}

/** Default floor for each side of a window: entries are recorded to the minute and caregivers round. */
export const MIN_HALF_WIDTH_MIN = 15;

export interface WindowStats {
  medianMin: number;
  lowerMin: number;
  upperMin: number;
  /** True when the empirical window was widened for sparse or variable data. */
  widened: boolean;
  effectiveSamples: number;
}

/**
 * Weighted median plus an empirical lower/upper window. The window is widened
 * by 25% on each side when the effective sample is below twice the minimum,
 * and by a further 25% when the spread exceeds 75% of the median. Each side
 * is then at least `minHalfWidthMin` wide (default 15 minutes).
 */
export function windowStats(samples: Sample[], opts: WindowOptions): WindowStats {
  const medianMin = weightedQuantile(samples, 0.5);
  const q0 = weightedQuantile(samples, opts.lowerQ);
  const q1 = weightedQuantile(samples, opts.upperQ);
  const effectiveSamples = effectiveSampleSize(samples);
  const sparse = effectiveSamples < 2 * opts.minSamples;
  const variable = medianMin > 0 && (q1 - q0) / medianMin > 0.75;
  const factor = 1 + (sparse ? 0.25 : 0) + (variable ? 0.25 : 0);
  const floor = opts.minHalfWidthMin ?? MIN_HALF_WIDTH_MIN;
  return {
    medianMin,
    lowerMin: Math.max(0, medianMin - Math.max(floor, (medianMin - q0) * factor)),
    upperMin: medianMin + Math.max(floor, (q1 - medianMin) * factor),
    widened: factor > 1,
    effectiveSamples,
  };
}

export function toWindow(basisMs: number, stats: Pick<WindowStats, 'medianMin' | 'lowerMin' | 'upperMin'>): PredictionWindow {
  return {
    pointMs: Math.round(basisMs + stats.medianMin * MINUTE_MS),
    startMs: Math.round(basisMs + stats.lowerMin * MINUTE_MS),
    endMs: Math.round(basisMs + stats.upperMin * MINUTE_MS),
  };
}

/** Plain (unweighted) median, used by the recent-median comparison baseline. */
export function median(values: number[]): number {
  return weightedQuantile(values.map((value) => ({ value, weight: 1 })), 0.5);
}
